import React, { useEffect, useState } from 'react'
import { withTranslation } from 'react-i18next'
import * as UIcons from '@iconscout/react-unicons'
import useSWR, { mutate } from 'swr'
import dayjs from 'dayjs'
import { CSVLink } from 'react-csv'
import Btn from 'components/Btn'
import PageHeader from '../shared/PageHeader'
import POSStats from './components/POSStats'
import POSFilterBar from './components/POSFilterBar'
import POSTable from './components/POSTable'
import AddPOS from './components/AddPOS'
import usePermissions from 'hooks/usePermissions'

export default withTranslation()(function POSManagement({ t }) {
  const { data, isValidating } = useSWR('pos')
  const { data: branchesRes } = useSWR('merchants/branches')
  const { isPermitted } = usePermissions()
  const [q, setQ] = useState('')
  const [branch, setBranch] = useState('')
  const [deviceModel, setDeviceModel] = useState('')
  const [status, setStatus] = useState('')
  const [showAdd, setShowAdd] = useState(false)
  const [lastUpdated, setLastUpdated] = useState(dayjs())

  const devices = data?.data ?? []
  const branches = branchesRes?.data ?? []
  const deviceModels = [...new Set(devices.map((device) => device.device_model).filter(Boolean))]

  useEffect(() => {
    if (!isValidating) setLastUpdated(dayjs())
  }, [isValidating])

  const refresh = () => {
    mutate('pos')
    mutate('merchants/branches')
  }

  const branchNameById = branches.reduce((acc, b) => ({ ...acc, [b.id]: b.name }), {})
  const csvData = devices.map((device) => ({
    pos_id: device.code,
    branch: branchNameById[device.branch_id] ?? '',
    assigned_operator: device.user ?? '',
    device_model: device.device_model ?? '',
    status: device.status ?? 'active'
  }))

  return (
    <section className="w-full">
      <PageHeader
        crumbs={[
          { name: t('settings.hub.title'), url: '/settings' },
          { name: t('settings.business_organization.title'), url: '/settings/business-organization' },
          { name: t('pos.management.page_title') }
        ]}
        title={t('pos.management.page_title')}
        right={
          <div className="flex items-center gap-2">
            <span className="text-sm text-secondary whitespace-nowrap">
              {t('pos.management.last_update', { date: lastUpdated.format('D MMM YY') })}
            </span>

            <button
              type="button"
              onClick={refresh}
              className="w-9 h-9 rounded-full border border-border-lines flex items-center justify-center hover:bg-background shrink-0"
            >
              <UIcons.UilRedo className="text-secondary" size={16} />
            </button>

            <CSVLink
              data={csvData}
              filename="pos-devices.csv"
              className="flex items-center gap-1.5 border border-border-lines rounded-full h-9 px-4 text-sm font-medium text-dark-blue whitespace-nowrap shrink-0"
            >
              <UIcons.UilExport size={16} /> {t('misc.export')}
            </CSVLink>

            <Btn
              content={() => <span className="flex items-center gap-1.5"><UIcons.UilPlus size={16} /> {t('pos.management.add')}</span>}
              className="text-white bg-primary rounded-full h-9 px-4 font-medium"
              click={() => setShowAdd(true)}
              disabled={!isPermitted?.create_pos}
            />
          </div>
        }
      />

      <POSStats />

      <POSFilterBar
        q={q}
        onChangeQ={setQ}
        branches={branches}
        branch={branch}
        onChangeBranch={setBranch}
        deviceModels={deviceModels}
        deviceModel={deviceModel}
        onChangeDeviceModel={setDeviceModel}
        status={status}
        onChangeStatus={setStatus}
      />

      <POSTable q={q} branch={branch} deviceModel={deviceModel} status={status} />

      {
        showAdd && (
          <AddPOS
            handleClick={() => setShowAdd(false)}
            onSaved={() => mutate('pos')}
          />
        )
      }
    </section>
  )
})
